import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Tooltip, Button } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import { Table } from '../../components/table/Table';
import { DownloadButton } from '../../components/DownloadButton';
import { FieldDetails } from './FieldDetails';
import {
  getRounded,
  normalizeToRange,
  ROUNDING_DIRECTIONS,
} from '../../lib/numbers';
import { normalizeStatus } from '../../lib/statuses';
import {
  downloadSoyRxPdfs,
  downloadSoyRxShapeFile,
} from '../../services/soyrx-service';
import { downloadProductSummary } from '../../services/product-placement-vpi-service';
import { generateCsvDownload } from '../../lib/csv';
import { usePermissions } from '../../hooks/use-permissions';
import { selectors, actions } from '../../store/dux/soyrx-dux';
import { useFields } from '../../hooks/use-fields';
import {
  actions as vpiActions,
  selectors as vpiSelectors,
} from '../../store/dux/product-placement-vpi-dux';
import { ChooserDialog } from '../../components/ChooserDialog';
import { VPIModal } from './VPIModal';
import { useGrowers } from '../../hooks/use-growers';
import { useFeatureFlag } from '../../hooks/use-feature-flag';
import { SendToDeereButton } from '../../components/deere/SendToDeereButton';
import { Choice } from '../../components/Choice';
import { VPIAssignProductsModal } from './VPIAssignProductsModal';
import { useMapRefresh } from '../../components/google/maps/use-map-refresh';
import {
  setSoyEarlyAdjustmentFactor,
  setSoyLateAdjustmentFactor,
  setSoyRxEarlyGroupAdjustment,
  setSoyRxLateGroupAdjustment,
} from '../../lib/api';
import { usePermission } from '../../hooks/use-permission';

export default function SoyRxGrid() {
  const dispatch = useDispatch();
  const permissions = usePermissions();
  const { selectedId: growerId } = useGrowers();
  const showActions = usePermission('VIEW_ACTIONS');
  const fields = useSelector(selectors.fields);
  const selectedSoyFields = useSelector(selectors.selectedSoyFields);
  const isLoading = useSelector(selectors.isLoading);
  const { selectedAaids, selectedFields } = useFields();
  const [isChoosingType, setChoosingType] = useState();
  const [chosenType, setChosenType] = useState();
  const isVpiEnabled = useFeatureFlag('ProductPlacementVPI');
  const isVPIAssignProductOpen = useSelector(vpiSelectors.isAssignmentOpen);
  const isVPIRecommendationOpen = useSelector(
    vpiSelectors.isRecommendationOpen,
  );
  const [adjustmentField, setAdjustmentField] = useState();
  const { refresh: refreshMap } = useMapRefresh();

  const setIsVPIRecommendationOpen = useCallback(
    (isOpen) => {
      dispatch(vpiActions.setRecommendationOpen(isOpen));
    },
    [dispatch],
  );

  const setIsVPIAssignProductOpen = useCallback(
    (isOpen) => {
      dispatch(vpiActions.setAssignmentOpen(isOpen));
    },
    [dispatch],
  );

  const getDownloadableAaids = useCallback(
    () =>
      selectedAaids.filter((aaid) => {
        const targetField = fields.find((f) => f.aaManagedId === aaid);
        return targetField?.seedTotalEarly || targetField?.seedTotalLate;
      }),
    [fields, selectedAaids],
  );

  const getDownloadablePrescriptions = useCallback(() => {
    const aaids = getDownloadableAaids();
    return fields.filter((f) => aaids.includes(f.aaManagedId));
  }, [fields, getDownloadableAaids]);

  const downloadPdf = useCallback(() => {
    setChoosingType(null);
    return downloadSoyRxPdfs(growerId, getDownloadableAaids(), chosenType);
  }, [chosenType, getDownloadableAaids, growerId]);

  const downloadShapefiles = useCallback(() => {
    setChoosingType(null);
    return downloadSoyRxShapeFile(growerId, getDownloadableAaids(), chosenType);
  }, [chosenType, getDownloadableAaids, growerId]);

  const downloadCsv = useCallback(() => {
    const filename = `soyrx-export-${growerId}.csv`;

    let csvArr = fields;
    if (selectedAaids.length > 0) {
      csvArr = fields.filter((f) =>
        selectedAaids.includes(f.aaManagedId.toUpperCase()),
      );
    }
    const csvData = csvArr.map((f) => ({
      field_name: f.fieldName,
      farm_name: f.farmName,
      field_id: f.fieldId,
      acres: getRounded(f.acres, 2),
      created: f.createdDate
        ? new Date(f.createdDate).toLocaleDateString()
        : '',
      min_rate_early: getRounded(f.minRateEarly, 0),
      max_rate_early: getRounded(f.maxRateEarly, 0),
      avg_rate_early: getRounded(f.avgRateEarly, 2),
      seed_total_early: getRounded(f.seedTotalEarly, 0),
      total_units_early: normalizeToRange(
        f.seedTotalUnitsEarly,
        1,
        ROUNDING_DIRECTIONS.UP,
        0,
        true,
      ),
      min_rate_late: getRounded(f.minRateLate, 0),
      max_rate_late: getRounded(f.maxRateLate, 0),
      avg_rate_late: getRounded(f.avgRateLate, 2),
      seed_total_late: getRounded(f.seedTotalLate, 0),
      total_units_late: normalizeToRange(
        f.seedTotalUnitsLate,
        1,
        ROUNDING_DIRECTIONS.UP,
        0,
        true,
      ),
      status: f.rxStatus ? normalizeStatus(f.rxStatus, f.hasSoyRx) : 'N/A',
      aaid: f.aaManagedId,
    }));

    generateCsvDownload(csvData, filename);
  }, [fields, growerId, selectedAaids]);

  const downloadProductSummaryPdf = useCallback(() => {
    setChoosingType(null);
    return downloadProductSummary(growerId, selectedAaids, chosenType);
  }, [chosenType, selectedAaids, growerId]);

  const shapefileAction = useMemo(
    () =>
      permissions.includes('DOWNLOAD_RX') && (
        <DownloadButton
          key="download-shapefiles"
          disabled={!getDownloadableAaids().length}
          preDownload={() => initChooser('shapefile')}
          postDownload={clearChooser}
          autoDownload={
            chosenType && isChoosingType === 'shapefile'
              ? downloadShapefiles
              : undefined
          }
          data-testid="download-btn"
          keep-open="true"
        >
          Download
        </DownloadButton>
      ),
    [
      isChoosingType,
      chosenType,
      downloadShapefiles,
      getDownloadableAaids,
      permissions,
    ],
  );

  const csvAction = useMemo(
    () => (
      <DownloadButton
        key="download-csv"
        download={downloadCsv}
        data-testid="export-btn"
      >
        Export CSV
      </DownloadButton>
    ),
    [downloadCsv],
  );

  const pdfAction = useMemo(
    () => (
      <DownloadButton
        key="download-pdf"
        disabled={!getDownloadableAaids().length}
        preDownload={() => initChooser('pdf')}
        postDownload={clearChooser}
        autoDownload={
          chosenType && isChoosingType === 'pdf' ? downloadPdf : undefined
        }
        data-testid="pdf-btn"
        keep-open="true"
      >
        Print to PDF
      </DownloadButton>
    ),
    [isChoosingType, chosenType, downloadPdf, getDownloadableAaids],
  );

  const vpiRecommendationAction = useMemo(
    () =>
      isVpiEnabled && (
        <Button
          data-testid="generate-recommendation"
          disabled={
            !(
              selectedFields.length > 0 &&
              selectedFields.every((field) =>
                field.additionalServices?.includes('VPI'),
              )
            )
          }
          onClick={() => setIsVPIRecommendationOpen(true)}
          keep-open="true"
        >
          Generate Variety Recommendation
        </Button>
      ),
    [isVpiEnabled, selectedFields, setIsVPIRecommendationOpen],
  );

  const deereAction = useMemo(
    () => (
      <SendToDeereButton
        type="soy"
        pattern="{fieldName}_soy_{timing}_{year}"
        prescriptions={getDownloadablePrescriptions()}
        prescriptionMapper={(options, rx) => ({ ...options, ...rx })}
        Options={EarlyOrLate}
      />
    ),
    [getDownloadablePrescriptions],
  );

  const vpiAssignProductAction = useMemo(
    () =>
      isVpiEnabled && (
        <Button
          data-testid="assign-product"
          disabled={selectedFields.length === 0}
          onClick={() => setIsVPIAssignProductOpen(true)}
          keep-open="true"
        >
          Assign Products
        </Button>
      ),
    [selectedFields, isVpiEnabled, setIsVPIAssignProductOpen],
  );

  const vpiPrintSummaryAction = useMemo(
    () =>
      isVpiEnabled && (
        <DownloadButton
          key="download-summary-pdf"
          disabled={
            selectedSoyFields.length === 0 ||
            selectedSoyFields.some((field) => !field.assignedProducts.length)
          }
          preDownload={() => initChooser('summaryPdf')}
          postDownload={clearChooser}
          autoDownload={
            chosenType && isChoosingType === 'summaryPdf'
              ? downloadProductSummaryPdf
              : undefined
          }
          keep-open="true"
        >
          Print Product Summary
        </DownloadButton>
      ),
    [
      isChoosingType,
      chosenType,
      downloadProductSummaryPdf,
      isVpiEnabled,
      selectedSoyFields,
    ],
  );

  const tableActions = useMemo(
    () =>
      [
        shapefileAction,
        csvAction,
        pdfAction,
        vpiRecommendationAction,
        deereAction,
        vpiAssignProductAction,
        vpiPrintSummaryAction,
      ].filter(Boolean),
    [
      shapefileAction,
      csvAction,
      pdfAction,
      vpiRecommendationAction,
      deereAction,
      vpiAssignProductAction,
      vpiPrintSummaryAction,
    ],
  );

  useEffect(() => {
    dispatch(actions.loadSoyRxForGrower(growerId));
  }, [dispatch, growerId]);

  return (
    <>
      <Table
        actions={showActions ? tableActions : undefined}
        columns={buildColumns()}
        fields={fields || []}
        loading={isLoading}
        isLpkContractRestricted={true}
      />
      {adjustmentField && (
        <FieldDetails
          field={adjustmentField}
          handleClose={() => setAdjustmentField(null)}
          handleSave={saveAdjustments}
        />
      )}
      {isVPIRecommendationOpen && (
        <VPIModal
          close={() => setIsVPIRecommendationOpen(false)}
          aaids={selectedAaids}
          growerId={growerId}
        />
      )}
      {isVPIAssignProductOpen && (
        <VPIAssignProductsModal
          close={() => {
            setIsVPIAssignProductOpen(false);
            dispatch(actions.loadSoyRxForGrower(growerId));
          }}
          growerId={growerId}
        />
      )}
      {buildChooser()}
    </>
  );

  async function saveAdjustments(
    aaid,
    earlyAdjustmentFactor,
    lateAdjustmentFactor,
    earlyAdjustmentOffsets,
    lateAdjustmentOffsets,
  ) {
    await Promise.all([
      setSoyEarlyAdjustmentFactor(aaid, earlyAdjustmentFactor),
      setSoyLateAdjustmentFactor(aaid, lateAdjustmentFactor),
      setSoyRxEarlyGroupAdjustment(aaid, earlyAdjustmentOffsets),
      setSoyRxLateGroupAdjustment(aaid, lateAdjustmentOffsets),
    ]);
    dispatch(actions.loadSoyRxForGrower(growerId));
    refreshMap();
  }

  function initChooser(type) {
    setChoosingType(type);
    setChosenType(null);
  }

  function clearChooser() {
    setChoosingType(null);
    setChosenType(null);
  }

  function chooseType(event, chosen) {
    event.stopPropagation();
    setChosenType(chosen);
  }

  function buildChooser() {
    if (!isChoosingType) return null;
    let direction;
    const isShapeFile = isChoosingType === 'shapefile';

    if (isShapeFile) {
      direction = `Select the shape files you wish to download`;
    } else if (isChoosingType === 'summaryPdf') {
      direction = `Select the summary you wish to print`;
    } else {
      direction = `Select the prescription you wish to print`;
    }

    return (
      <ChooserDialog
        title={isShapeFile ? 'Download' : 'Print to PDF'}
        direction={direction}
        onCancel={clearChooser}
        onChoose={chooseType}
        options={[
          { value: 'early', label: 'Soy Early' },
          { value: 'late', label: 'Soy Late' },
        ]}
      />
    );
  }

  function buildColumns() {
    return [
      {
        field: 'plannedCrop',
        headerName: 'Planned Crop',
        description: 'plannedCrop',
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'createdDate',
        headerName: 'Created',
        description: 'Created',
        flex: 1,
        minWidth: 100,
        type: 'date',
        align: 'right',
        valueFormatter: ({ value }) =>
          value ? new Date(value).toLocaleDateString() : '',
      },
      {
        field: 'minRateEarly',
        headerName: 'Early Min Rate',
        description: 'Early Min Rate',
        flex: 1,
        minWidth: 80,
        type: 'number',
        valueFormatter: ({ value }) =>
          normalizeToRange(value, 5000, ROUNDING_DIRECTIONS.UP, 90000, true),
      },
      {
        field: 'maxRateEarly',
        headerName: 'Early Max Rate',
        description: 'Early Max Rate',
        flex: 1,
        minWidth: 80,
        type: 'number',
        valueFormatter: ({ value }) =>
          normalizeToRange(value, 5000, ROUNDING_DIRECTIONS.UP, 90000, true),
      },
      {
        field: 'avgRateEarly',
        headerName: 'Early Avg Rate',
        description: 'Early Avg Rate',
        flex: 1,
        minWidth: 80,
        type: 'number',
        valueFormatter: ({ value }) => getRounded(value, 0, true),
      },
      {
        field: 'seedTotalEarly',
        headerName: 'Early Seed Total',
        description: 'Early Seed Total',
        flex: 1,
        minWidth: 100,
        type: 'number',
        valueFormatter: ({ value }) => getRounded(value, 0, true),
      },
      {
        field: 'seedTotalUnitsEarly',
        headerName: 'Early Total Units',
        description: 'Early Total Units',
        flex: 1,
        minWidth: 60,
        type: 'number',
        valueFormatter: ({ value }) =>
          normalizeToRange(value, 1, ROUNDING_DIRECTIONS.UP, 0, true),
      },
      {
        field: 'minRateLate',
        headerName: 'Late Min Rate',
        description: 'Late Min Rate',
        flex: 1,
        minWidth: 80,
        type: 'number',
        valueFormatter: ({ value }) =>
          normalizeToRange(value, 5000, ROUNDING_DIRECTIONS.UP, 90000, true),
      },
      {
        field: 'maxRateLate',
        headerName: 'Late Max Rate',
        description: 'Late Max Rate',
        flex: 1,
        minWidth: 80,
        type: 'number',
        valueFormatter: ({ value }) =>
          normalizeToRange(value, 5000, ROUNDING_DIRECTIONS.UP, 90000, true),
      },
      {
        field: 'avgRateLate',
        headerName: 'Late Avg Rate',
        description: 'Late Avg Rate',
        flex: 1,
        minWidth: 80,
        type: 'number',
        valueFormatter: ({ value }) => getRounded(value, 0, true),
      },
      {
        field: 'seedTotalLate',
        headerName: 'Late Seed Total',
        description: 'Late Seed Total',
        flex: 1,
        minWidth: 100,
        type: 'number',
        valueFormatter: ({ value }) => getRounded(value, 0, true),
      },
      {
        field: 'seedTotalUnitsLate',
        headerName: 'Late Total Units',
        description: 'Late Total Units',
        flex: 1,
        minWidth: 60,
        type: 'number',
        valueFormatter: ({ value }) =>
          normalizeToRange(value, 1, ROUNDING_DIRECTIONS.UP, 0, true),
      },
      {
        field: 'rxStatus',
        headerName: 'Rx Status',
        description: 'Rx Status',
        flex: 1,
        renderCell: ({ value, row }) => <SoyRxStatus value={value} row={row} />,
      },
      isVpiEnabled && {
        field: 'product',
        headerName: 'Product',
        description: 'Product',
        flex: 1,
        renderCell: ({ row }) => <AssignedProducts row={row} />,
      },
      {
        field: 'actions',
        type: 'actions',
        flex: 1,
        minWidth: 60,
        headerName: 'Adjust',
        description: 'Adjust',
        getActions: ({ row }) => [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Adjust"
            onClick={() => setAdjustmentField(row)}
            disabled={!row.hasSoyRx}
            data-testid={`adjust-${row.aaManagedId}`}
          />,
        ],
      },
    ].filter(Boolean);
  }
}

function SoyRxStatus({ value, row }) {
  const normalized = normalizeStatus(value, row.hasSoyRx);
  return (
    <>
      {normalized}
      {normalized === 'Failed' && (
        <Tooltip title={value}>
          <InfoIcon />
        </Tooltip>
      )}
    </>
  );
}

function AssignedProducts({ row }) {
  if (!row.assignedProducts || row.assignedProducts.length === 0) return '--';
  const multiDisplay = row.assignedProducts
    .map(
      ({ product, percent }, i) => `Product ${i + 1}: ${product} ${percent}%`,
    )
    .join('\n');

  return row.assignedProducts.length === 1 ? (
    <Box>{row.assignedProducts[0].product}</Box>
  ) : (
    <Tooltip
      title={
        <Box data-testid={'multiple-products'} sx={{ whiteSpace: 'pre-line' }}>
          {multiDisplay}
        </Box>
      }
      disableFocusListener
    >
      <Box>Multiple</Box>
    </Tooltip>
  );
}

function EarlyOrLate({ setOptions }) {
  const options = [
    { value: 'early', label: 'Soy Early' },
    { value: 'late', label: 'Soy Late' },
  ];
  const [timing, setTiming] = useState(null);

  useEffect(() => {
    if (timing) setOptions({ timing });
  }, [timing, setOptions]);

  return (
    <Box
      component="ul"
      sx={{
        width: 200,
        listStyle: 'none',
        p: 0,
        '& li': { width: '100%' },
        '& li + li': { mt: 2 },
      }}
    >
      {options.map((option, key) => (
        <Choice option={option} key={key} onSelect={setTiming} />
      ))}
    </Box>
  );
}
